/**
 * Billing — a read-only entitlement readout (TRE-781 GET /entitlement). No mutation
 * surface in v1: the tier flip is operator/server-side (broker-gated), not reachable
 * from a user session. Seat counts derive from /account/members. This screen is
 * shown to owners (manage_billing); the data itself is the account's own gate.
 */

import { api } from "../api/client";
import type { AccountContext } from "../api/types";
import { tierLabel } from "../lib/capabilities";
import { useAsyncData } from "../lib/useAsyncData";

export function Billing({ ctx: _ctx }: { ctx: AccountContext }) {
  const ent = useAsyncData(() => api.entitlement(), []);
  const members = useAsyncData(() => api.members(), []);

  return (
    <div>
      <h1>Billing</h1>
      <p className="screen-intro">
        Your account's current plan and entitlement. To change plans, contact Treillage — billing
        changes are made server-side.
      </p>

      {ent.loading && <p className="muted">Loading entitlement…</p>}
      {ent.error && (
        <div className="banner banner--error" role="alert">
          {ent.error} <button className="btn--link" onClick={ent.reload}>Retry</button>
        </div>
      )}

      {ent.value && (
        <table className="table">
          <tbody>
            <tr>
              <th>Plan</th>
              <td>{tierLabel(ent.value.tier)}</td>
            </tr>
            <tr>
              <th>Status</th>
              <td>
                <span
                  className={`chip ${ent.value.entitled ? "chip--creator" : "chip--runner"}`}
                  data-glyph={ent.value.entitled ? "✓" : "—"}
                >
                  {ent.value.entitled ? "Active" : "Not entitled"}
                </span>
              </td>
            </tr>
            <tr>
              <th>Authoring</th>
              <td>{ent.value.authoring ? "Enabled" : "Run-only"}</td>
            </tr>
            <tr>
              <th>Seats in use</th>
              <td>
                {members.value ? (
                  <>
                    {members.value.count} member{members.value.count === 1 ? "" : "s"} (
                    {members.value.ownerCount} owner{members.value.ownerCount === 1 ? "" : "s"})
                  </>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
